import React, { useState, useMemo } from 'react';
import { Table, TableColumn, Pagination } from '@components/common/table';
import { Button } from '@components/common/button';
import { Input } from '@components/common/input';
import {
  Plus,
  Search,
  Filter,
  Download,
  Eye,
  Edit,
  Trash2,
  Gauge,
  Activity,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Clock
} from 'lucide-react';
import { Meter, MeterStatus } from '@/types/meter_types';

interface MeterListProps {
  meters: Meter[];
  is_loading?: boolean;
  on_add_meter: () => void;
  on_view_meter: (meter: Meter) => void;
  on_edit_meter: (meter: Meter) => void;
  on_delete_meter: (meter: Meter) => void;
  on_add_reading: (meter: Meter) => void;
  on_export?: () => void;
}

export const MeterList: React.FC<MeterListProps> = ({
  meters,
  is_loading = false,
  on_add_meter,
  on_view_meter,
  on_edit_meter,
  on_delete_meter,
  on_add_reading,
  on_export
}) => {
  const [search_term, set_search_term] = useState('');
  const [filter_status, set_filter_status] = useState<MeterStatus | 'all'>('all');
  const [current_page, set_current_page] = useState(1);
  const [sort_config, set_sort_config] = useState<{
    key: string;
    direction: 'asc' | 'desc';
  }>({ key: 'meter_number', direction: 'asc' });

  const items_per_page = 10;

  // Helper function to get last reading value
  const get_last_reading = (meter: Meter): number | null => {
    if ((meter as any).latest_reading?.current_reading) {
      return parseFloat((meter as any).latest_reading.current_reading);
    }
    return null;
  };

  // Helper function to get consumption
  const get_consumption = (meter: Meter): number | null => {
    if ((meter as any).latest_reading?.consumption) {
      const consumption = parseFloat((meter as any).latest_reading.consumption);
      return Math.abs(consumption); // Use absolute value to avoid negative display
    }
    return null;
  };

  // Filter and search meters
  const filtered_meters = useMemo(() => {
    let result = [...meters];

    // Apply status filter
    if (filter_status !== 'all') {
      result = result.filter(meter => meter.status === filter_status);
    }

    // Apply search
    if (search_term) {
      const term = search_term?.toLowerCase();
      result = result.filter(meter =>
        meter.meter_number?.toLowerCase().includes(term) ||
        meter.client_name?.toLowerCase().includes(term) ||
        meter.notes?.toLowerCase().includes(term)
      );
    }

    // Apply sorting
    result.sort((a, b) => {
      let a_value: any;
      let b_value: any;

      // Handle special sorting for latest_reading fields
      if (sort_config.key === 'last_reading_value') {
        a_value = get_last_reading(a);
        b_value = get_last_reading(b);
      } else {
        a_value = a[sort_config.key as keyof Meter];
        b_value = b[sort_config.key as keyof Meter];
      }

      if (a_value === undefined || a_value === null) return 1;
      if (b_value === undefined || b_value === null) return -1;

      if (a_value < b_value) return sort_config.direction === 'asc' ? -1 : 1;
      if (a_value > b_value) return sort_config.direction === 'asc' ? 1 : -1;
      return 0;
    });

    return result;
  }, [meters, filter_status, search_term, sort_config]);

  // Pagination
  const total_pages = Math.ceil(filtered_meters.length / items_per_page);
  const paginated_meters = filtered_meters.slice(
    (current_page - 1) * items_per_page,
    current_page * items_per_page
  );

  const handle_sort = (key: string) => {
    set_sort_config(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const get_status_badge = (status: MeterStatus) => {
    const status_lower = status.toLowerCase();
    
    const styles: Record<string, string> = {
      active: 'bg-success bg-opacity-10 text-success',
      inactive: 'bg-gray-100 text-gray-600',
      faulty: 'bg-error bg-opacity-10 text-error',
      maintenance: 'bg-warning bg-opacity-10 text-warning'
    };

    const icons: Record<string, React.ReactNode> = {
      active: <CheckCircle size={14} />,
      inactive: <XCircle size={14} />,
      faulty: <AlertTriangle size={14} />,
      maintenance: <Clock size={14} />
    };

    return (
      <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold ${styles[status_lower]}`}>
        {icons[status_lower]}
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const needs_reading = (meter: Meter) => {
    if (!meter.last_reading_date) return true;
    const days_since_reading = Math.floor(
      (Date.now() - new Date(meter.last_reading_date).getTime()) / (1000 * 60 * 60 * 24)
    );
    return days_since_reading > 30;
  };

  const columns: TableColumn<Meter>[] = [
    {
      key: 'meter_number',
      header: 'Meter Number',
      sortable: true,
      render: (meter) => (
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-forest-green bg-opacity-10 text-forest-green flex items-center justify-center">
            <Gauge size={20} />
          </div>
          <div>
            <p className="font-semibold text-charcoal">{meter.meter_number}</p>
            {meter.notes && (
              <p className="text-xs text-gray-500">{meter.notes.split(' | ')[0]}</p>
            )}
          </div>
        </div>
      )
    },
    {
      key: 'client_name',
      header: 'Client',
      sortable: true,
      render: (meter) => (
        <div>
          <p className="font-medium text-charcoal">{meter.client_name}</p>
          {meter.notes && (
            <p className="text-xs text-gray-500">
              {meter.notes.split(' | ').slice(2).join(' - ')}
            </p>
          )}
        </div>
      )
    },
    {
      key: 'last_reading_value',
      header: 'Last Reading',
      sortable: true,
      align: 'right',
      render: (meter) => {
        const last_reading = get_last_reading(meter);
        return (
          <div className="text-right">
            {last_reading !== null ? (
              <>
                <p className="font-semibold text-charcoal">
                  {last_reading.toLocaleString()} units
                </p>
                {meter.last_reading_date && (
                  <p className="text-xs text-gray-500">
                    {new Date(meter.last_reading_date).toLocaleDateString()}
                  </p>
                )}
              </>
            ) : (
              <span className="text-sm text-gray-400 italic">No readings</span>
            )}
          </div>
        );
      }
    },
    {
      key: 'consumption',
      header: 'Consumption',
      align: 'right',
      render: (meter) => {
        const consumption = get_consumption(meter);
        return (
          <div className="text-right">
            {consumption !== null && consumption > 0 ? (
              <p className="font-semibold text-forest-green">
                {consumption.toLocaleString()} units
              </p>
            ) : (
              <span className="text-sm text-gray-400">-</span>
            )}
          </div>
        );
      }
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      render: (meter) => (
        <div className="flex flex-col gap-1">
          {get_status_badge(meter.status)}
          {needs_reading(meter) && (meter as any).status === 'ACTIVE' && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-orange-100 text-orange-600 rounded-full text-xs font-medium">
              <Activity size={12} />
              Reading due
            </span>
          )}
        </div>
      )
    },
    {
      key: 'actions',
      header: 'Actions',
      align: 'right',
      render: (meter) => (
        <div className="flex items-center justify-end gap-1">
          {(meter as any).status === 'ACTIVE' && (
            <Button
              size="sm"
              variant="primary"
              onClick={(e) => {
                e.stopPropagation();
                on_add_reading(meter);
              }}
              icon_left={<Activity size={14} />}
              className="text-xs"
            >
              Read
            </Button>
          )}
          <Button
            size="sm"
            variant="ghost"
            onClick={(e) => {
              e.stopPropagation();
              on_view_meter(meter);
            }}
            icon_left={<Eye size={14} />}
            className="text-gray-600"
          >
            View
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={(e) => {
              e.stopPropagation();
              on_edit_meter(meter);
            }}
            icon_left={<Edit size={14} />}
            className="text-forest-green"
          >
            Edit
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={(e) => {
              e.stopPropagation();
              on_delete_meter(meter);
            }}
            icon_left={<Trash2 size={14} />}
            className="text-error"
          >
            Delete
          </Button>
        </div>
      )
    }
  ];

  // Calculate stats
  const stats = {
    total: filtered_meters.length,
    active: filtered_meters.filter(m => (m as any).status === 'ACTIVE').length,
    faulty: filtered_meters.filter(m => (m as any).status === 'FAULTY').length,
    needs_reading: filtered_meters.filter(m => needs_reading(m) && (m as any).status === 'ACTIVE').length
  };

  return (
    <div className="space-y-4">
      {/* Header Actions */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <div className="flex-1 w-full sm:max-w-md">
          <Input
            type="text"
            placeholder="Search by meter number, client, location..."
            value={search_term}
            onChange={(e) => set_search_term(e.target.value)}
            icon_left={<Search size={18} />}
          />
        </div>

        <div className="flex flex-wrap gap-2">
          {/* Filter by Status */}
          <div className="flex items-center gap-2 bg-white px-3 py-2 rounded-lg border border-gray-300">
            <Filter size={16} className="text-gray-500" />
            <select
              value={filter_status}
              onChange={(e) => set_filter_status(e.target.value as MeterStatus | 'all')}
              className="text-sm border-none focus:outline-none cursor-pointer"
            >
              <option value="all">All Status</option>
              <option value="ACTIVE">Active</option>
              <option value="INACTIVE">Inactive</option>
              <option value="FAULTY">Faulty</option>
            </select>
          </div>

          {on_export && (
            <Button
              variant="outline"
              size="md"
              icon_left={<Download size={18} />}
              onClick={on_export}
            >
              Export
            </Button>
          )}

          <Button
            variant="primary"
            size="md"
            icon_left={<Plus size={18} />}
            onClick={on_add_meter}
          >
            Add Meter
          </Button>
        </div>
      </div>

      {/* Stats Summary */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <p className="text-sm text-gray-500">Total Meters</p>
          <p className="text-2xl font-bold text-charcoal">{stats.total}</p>
        </div>
        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <p className="text-sm text-gray-500">Active</p>
          <p className="text-2xl font-bold text-success">{stats.active}</p>
        </div>
        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <p className="text-sm text-gray-500">Faulty</p>
          <p className="text-2xl font-bold text-error">{stats.faulty}</p>
        </div>
        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <p className="text-sm text-gray-500">Need Reading</p>
          <p className="text-2xl font-bold text-warning">{stats.needs_reading}</p>
        </div>
      </div>

      {/* Table */}
      <Table
        data={paginated_meters}
        columns={columns}
        is_loading={is_loading}
        on_row_click={on_view_meter}
        sort_config={sort_config}
        on_sort={handle_sort}
        empty_message="No meters found. Add your first meter to get started!"
        striped
        hoverable
      />

      {/* Pagination */}
      {total_pages > 1 && (
        <Pagination
          current_page={current_page}
          total_pages={total_pages}
          on_page_change={set_current_page}
          items_per_page={items_per_page}
          total_items={filtered_meters.length}
        />
      )}
    </div>
  );
};